import { useCallback, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import ExpandMoreSharp from '@mui/icons-material/ExpandMoreSharp';
import { Colors, Spacings } from 'styles';
import Fieldset from 'common/ui/Fieldset';
import Text from 'common/ui/Text';
import Input from 'common/ui/Input';
import { Stack } from 'common/ui/Layout';
import StudyDescriptions from 'common/StudyDescriptions';
import { useSelectedStudyDescriptions } from 'hooks/useSelectedStudyDescriptions';
import { Button } from 'common/ui/Button';
import type { HangingProtocol } from '../../../HangingProtocol/useHangingProtocol';

import { ClassificationCriteria } from './ClassificationCriteria';
import type { Layouts } from '../../StudyLoader/viewerLoaderState';
import { useSubmitForm } from './hooks/useSubmitForm';
import {
  HANGING_PROTOCOL_PHASE,
  POWERED_BY_SIRONA_CLASSIFICATIONS,
  SUBMIT_FORM_INTENT,
} from './constants';
import uniq from 'ramda/es/uniq';
import type { HangingProtocolPhase, SubmitFormIntent } from './constants';
import type { FlattenSimpleInterpolation } from 'styled-components';
import { useShouldUpdateHangingProtocolClassifications } from './hooks/useUpdateHangingProtocolClassifications';
import CircularProgress from '@mui/material/CircularProgress';
import { useCurrentCaseContext } from 'common/CurrentCase/CurrentCaseProvider';
import { telemetryContextFor } from 'modules/Apollo/types';
import spacings from 'styles/spacings';
import { Divider } from 'common/ui/Divider';
import { useNumberOfPriors } from './utils/useNumberOfPriors';

const accordionSx = {
  boxShadow: 'none',
  backgroundColor: 'transparent',
  '&.Mui-expanded': { margin: 0 },
  '&:before': {
    display: 'none',
  },
};

const StyledCircularProgressWrapper = styled.span`
  margin-right: ${Spacings.xsmall}rem;
`;

const disabledInputLabelCss = css`
  color: ${Colors.gray8};
`;

const disabledInputCss = css`
  ${disabledInputLabelCss}
  border-color: ${Colors.gray4};
`;

const fieldsetCss = css`
  margin-bottom: ${Spacings.xsmall}rem;
  width: 100%;
  padding: 0px ${Spacings.medium}rem;
`;

const fieldLabelCss = css`
  margin-top: 10px;
  margin-bottom: 5px !important;
`;

const switchCss = css`
  margin-left: 0px;
  margin-right: -${Spacings.xsmall}rem;
  margin-bottom: ${Spacings.xsmall}rem;
`;

export type HangingProtocolsFormProps = {
  // Second arg (createdHangingProtocolSmid) is supplied on CREATE by both forms, and only when the
  // post-create match tied with an older same-criteria HP; it asks the widget to pin the new HP as
  // active (VX-4713). The widget's single onSubmit handler serves both forms (see
  // HangingProtocolWidget's hangingProtocolsFormProps).
  onSubmit?: (HangingProtocolName: string, createdHangingProtocolSmid?: string) => void;
  onClose?: () => void;
  onError?: (err: Error) => void;
  hangingProtocol: Nullable<HangingProtocol>;
  mode: SubmitFormIntent;
  layouts: Layouts;
  studyDescriptions: Array<string>;
  groupedStudies: Array<string>;
  className?: string;
  css?: FlattenSimpleInterpolation;
  phase?: HangingProtocolPhase;
};

export const HangingProtocolsForm = ({
  layouts,
  studyDescriptions,
  groupedStudies,
  hangingProtocol,
  mode,
  onSubmit,
  onClose,
  onError,
  className,
  css: cssProp,
  phase,
}: HangingProtocolsFormProps): React.ReactElement => {
  const numberOfPriors = useNumberOfPriors({ context: APOLLO_QUERY_CONTEXT });
  const [, setStudyDescriptions] = useSelectedStudyDescriptions();
  const { currentCase } = useCurrentCaseContext();

  const [shouldUpdateClassifications, setShouldUpdateClassifications] =
    useShouldUpdateHangingProtocolClassifications();

  const [includeLayoutChanges, setIncludeLayoutChanges] = useState(true);
  const primaryStudy = currentCase?.studies[0];

  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSuccess = useCallback(
    (hangingProtocolName: string, createdHangingProtocolSmid?: string) => {
      if (primaryStudy == null) {
        return;
      }
      setIncludeLayoutChanges(true);
      setShouldUpdateClassifications(true);

      const updatedStudyDescriptions =
        hangingProtocol != null &&
        primaryStudy.description != null &&
        hangingProtocol.studyDescriptions != null
          ? uniq([...hangingProtocol.studyDescriptions, primaryStudy.description])
          : studyDescriptions;

      setStudyDescriptions(updatedStudyDescriptions);

      onSubmit?.(hangingProtocolName, createdHangingProtocolSmid);
      setIsSubmitting(false);
    },
    [
      hangingProtocol,
      onSubmit,
      primaryStudy,
      setShouldUpdateClassifications,
      setStudyDescriptions,
      studyDescriptions,
    ]
  );

  const submitForm = useSubmitForm({
    hangingProtocol,
    intent: mode,
    currentCase,
    shouldUpdateClassifications,
    numberOfPriors,
    includeLayoutChanges,
    layouts,
    studyDescriptions,
    groupedStudies,
    onComplete: onSuccess,
    onError: (err) => {
      setIsSubmitting(false);
      onError?.(err);
    },
    context: APOLLO_QUERY_CONTEXT,
  });

  const formCss = css`
    background-color: ${Colors.gray3};
    background-image: none;
    padding: ${spacings.medium}rem 0px;
    margin: 0;
    max-height: 95vh;
    overflow: hidden;
    ${cssProp}
  `;

  useEffect(() => {
    if (hangingProtocol?.studyDescriptions != null) {
      setStudyDescriptions([...hangingProtocol.studyDescriptions]);
    } else if (studyDescriptions.length > 0) {
      setStudyDescriptions(studyDescriptions);
    } else {
      setStudyDescriptions([]);
    }
  }, [hangingProtocol, setStudyDescriptions, studyDescriptions]);

  return (
    // @ts-expect-error [EN-10263] - TS2769 - No overload matches this call.
    <Stack vertical stretchY className={className} css={formCss}>
      <Stack vertical css={{ padding: `0px ${Spacings.medium}rem` }}>
        <Text
          variant="display2"
          css={`
            padding-left: ${Spacings.xsmall}rem;
          `}
        >
          {mode === SUBMIT_FORM_INTENT.UPDATE ? 'Edit' : 'Create'} Hanging Protocol
        </Text>
        <Text
          css={`
            padding: 0 ${Spacings.small}rem ${Spacings.medium}rem ${Spacings.xsmall}rem;
          `}
        >
          {POWERED_BY_SIRONA_CLASSIFICATIONS}
        </Text>
      </Stack>
      <form
        css={css`
          display: flex;
          flex-direction: column;
          flex: 1;
          min-height: 0; /* Important for flex child to be scrollable */
        `}
        // @ts-expect-error [EN-7967] - TS2322 - Type '{ children: Element[]; css: string; disabled: boolean; onSubmit: (e: FormEvent<HTMLFormElement>) => void; "data-testid": string; }' is not assignable to type 'DetailedHTMLProps<FormHTMLAttributes<HTMLFormElement>, HTMLFormElement>'.
        disabled={isSubmitting}
        onSubmit={(e) => {
          e.preventDefault();
          setIsSubmitting(true);
          submitForm(e);
        }}
        data-testid="save-hanging-protocol-form"
      >
        <div
          css={css`
            flex: 1;
            overflow-y: auto;
            padding-right: ${Spacings.xsmall}rem;
            padding-left: ${Spacings.xsmall}rem;
          `}
        >
          <Stack
            vertical
            css={`
              padding: 0 ${Spacings.small}rem;
            `}
          >
            <Fieldset css={fieldsetCss}>
              <Text variant="caption" css={fieldLabelCss}>
                Hanging protocol name
              </Text>
              <Input
                name="name"
                required
                defaultValue={mode === SUBMIT_FORM_INTENT.UPDATE ? hangingProtocol?.name : ''}
                key={hangingProtocol?.smid}
                data-testid="protocol-field"
                css={`
                  background-color: ${Colors.gray1};
                `}
              />
            </Fieldset>
            <Fieldset css={fieldsetCss}>
              <Text
                variant="caption"
                css={css`
                  ${disabledInputLabelCss} ${fieldLabelCss}
                `}
              >
                Number of priors
              </Text>
              <Input
                name="numberOfPriors"
                defaultValue={numberOfPriors}
                data-testid="protocol-field-number-of-priors"
                css={disabledInputCss}
                disabled
              />
            </Fieldset>
            <Divider
              vertical={false}
              css={`
                margin-top: ${Spacings.medium}rem;
              `}
            />
            <Accordion defaultExpanded sx={accordionSx}>
              <AccordionSummary
                expandIcon={<ExpandMoreSharp />}
                sx={{ padding: `0px ${spacings.small}rem` }}
              >
                <Text variant="display1">Study Classification Criteria</Text>
              </AccordionSummary>
              <AccordionDetails sx={{ padding: `0px ${spacings.small}rem` }}>
                <Stack vertical space="small">
                  {mode === SUBMIT_FORM_INTENT.UPDATE && (
                    <FormControlLabel
                      label={
                        <span css="font-size: 14px;">
                          Update classifications from current study
                        </span>
                      }
                      control={
                        <Switch
                          checked={shouldUpdateClassifications}
                          onChange={(evt) => setShouldUpdateClassifications(evt.target.checked)}
                        />
                      }
                      labelPlacement="start"
                      css={switchCss}
                    />
                  )}
                  <ClassificationCriteria
                    showCurrentCaseClassification={
                      mode === SUBMIT_FORM_INTENT.SAVE || shouldUpdateClassifications
                    }
                    // @ts-expect-error [EN-7967] - TS2322 - Type 'JSON' is not assignable to type '{ [key: string]: ClassificationSummary[]; }'.
                    hangingProtocolClassification={hangingProtocol?.studyCriteria}
                  />
                </Stack>
              </AccordionDetails>
            </Accordion>
            <Divider
              vertical={false}
              css={`
                margin-top: ${Spacings.small}rem;
              `}
            />
            <Accordion defaultExpanded sx={accordionSx}>
              <AccordionSummary
                expandIcon={<ExpandMoreSharp />}
                sx={{ padding: `0px ${spacings.small}rem` }}
              >
                <Text variant="display1">Fall-back Criteria</Text>
              </AccordionSummary>
              <AccordionDetails sx={{ padding: `0px ${spacings.small}rem` }}>
                <Fieldset css={fieldsetCss}>
                  <Stack vertical space="xxsmall">
                    <Text variant="caption" css="margin-bottom: 4px !important">
                      Apply to selected study descriptions
                    </Text>
                    <StudyDescriptions data-testid="study-field" />
                  </Stack>
                </Fieldset>
              </AccordionDetails>
            </Accordion>

            {mode === SUBMIT_FORM_INTENT.UPDATE &&
              (phase === HANGING_PROTOCOL_PHASE.THREE ? (
                <>
                  <Divider
                    vertical={false}
                    css={`
                      margin-top: ${Spacings.small}rem;
                    `}
                  />
                  <Accordion defaultExpanded sx={accordionSx}>
                    <AccordionSummary expandIcon={<ExpandMoreSharp />}>
                      <Text variant="display1">Viewport Configuration</Text>
                    </AccordionSummary>
                    <AccordionDetails sx={{ padding: `0px ${spacings.small}rem` }}>
                      <FormControlLabel
                        label={
                          <span css="font-size: 14px;">
                            Update layout and viewport criteria from currently hung series
                          </span>
                        }
                        control={
                          <Switch
                            checked={includeLayoutChanges}
                            onChange={(evt) => setIncludeLayoutChanges(evt.target.checked)}
                          />
                        }
                        labelPlacement="start"
                        css={switchCss}
                      />
                    </AccordionDetails>
                  </Accordion>
                </>
              ) : (
                <>
                  <Divider
                    vertical={false}
                    css={`
                      margin-top: ${Spacings.small}rem;
                    `}
                  />
                  <label>
                    <Checkbox
                      color="primary"
                      size="small"
                      sx={{
                        color: Colors.blue4,
                      }}
                      checked={includeLayoutChanges}
                      onChange={(evt) => setIncludeLayoutChanges(evt.target.checked)}
                    />
                    Include layout changes
                  </label>
                </>
              ))}
          </Stack>
        </div>
        <Stack
          alignY="end"
          alignX="end"
          css={css`
            padding: ${Spacings.small}rem;
            padding-right: ${Spacings.medium}rem;
            flex-shrink: 0;
          `}
        >
          <Button
            type="button"
            variant="ghost"
            onClick={() => onClose?.()}
            data-testid="cancel-hanging-protocol-form"
            css={`
              border: none;
              margin-right: ${Spacings.small}rem !important;
            `}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            data-testid="submit-hanging-protocol-form"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <StyledCircularProgressWrapper data-testid="submit-hanging-protocol-spinner">
                <CircularProgress color="inherit" size={16} />
              </StyledCircularProgressWrapper>
            ) : null}
            {isSubmitting
              ? 'Submitting...'
              : mode === SUBMIT_FORM_INTENT.UPDATE
                ? 'Update'
                : 'Create'}
          </Button>
        </Stack>
      </form>
    </Stack>
  );
};

HangingProtocolsForm.displayName = 'HangingProtocolsForm';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(HangingProtocolsForm);
